import { useMemo } from 'react';
import { isEmpty } from '@iterable/utils';

type FieldInfo = {
  /* field value or conditions to watch */
  isValid: any;
  /* value to display if cloned value is missing */
  displayValue?: any;
};

/**
 * Hook that checks if any node data was uncloned
 * and returns validation messaging to display to the user
 *
 * @param {object} clonedFromNodeData - data used to compare
 * @param {object} fieldsToValidate - node data fields to watch
 * @param {string} nodeClass - in cases like start node, we also need to watch if nodeClass changes
 * @returns {boolean} isCloneWarningBannerShown - whether to show the warning banner
 * @returns {object} cloneErrors - an object to check if the watched field has an error
 * @returns {object} cloneDisplayValues - values to display as placeholders
 */
export const useDisplayCloneErrors = ({
  clonedFromNodeData = {},
  fieldsToValidate = {},
  nodeClass,
}: {
  clonedFromNodeData: any;
  fieldsToValidate: Record<string, FieldInfo>;
  nodeClass?: string;
}) => {
  const isNodeClassChangedFromClone = useMemo(
    () => nodeClass && nodeClass !== clonedFromNodeData?.nodeClass,
    [nodeClass, clonedFromNodeData],
  );

  const hideCloneErrors = useMemo(
    () => isEmpty(clonedFromNodeData) || isNodeClassChangedFromClone,
    [clonedFromNodeData, isNodeClassChangedFromClone],
  );

  const fields = useMemo(() => Object.entries(fieldsToValidate), [fieldsToValidate]);

  const cloneErrors: Record<string, boolean> = useMemo(() => {
    if (hideCloneErrors) return {};
    return fields.reduce(
      (acc, [field, val]) => ({
        ...acc,
        [field]: !!clonedFromNodeData[field] && !val.isValid,
      }),
      {},
    );
  }, [fieldsToValidate, fields, clonedFromNodeData, hideCloneErrors]);

  const cloneDisplayValues: Record<string, any> = useMemo(() => {
    if (hideCloneErrors) return {};
    return fields.reduce(
      (acc, [field, val]) => ({
        ...acc,
        [field]: val.displayValue || clonedFromNodeData[field],
      }),
      {},
    );
  }, [fieldsToValidate, fields, clonedFromNodeData, hideCloneErrors]);

  const isCloneWarningBannerShown = useMemo(
    () => Object.values(cloneErrors).some((field) => !!field),
    [cloneErrors],
  );

  return { isCloneWarningBannerShown, cloneErrors, cloneDisplayValues };
};
